import React, { useState, useEffect, useMemo } from 'react';
import * as Styles from './styles';
import { Button, Input, Modal, Select } from 'antd';
import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { closeModal } from 'services/store/reducers/modalReducer';
import {
  ModalState,
  InputEvent,
  TableVrUser,
  School,
  VrUser,
  Admin,
  EAdminRoles
} from 'shared/types';
import { getIn, useFormik } from 'formik';
import { validation } from 'services/validation';
import { getFieldError } from 'utils/error-utils';
import { addNewUser, updateUserItem } from '../../../../services/store/reducers/usersReducer';
import _ from 'lodash';

type Props = {
  user?: TableVrUser;
  schools: School[];
  vrUsers: VrUser[];
  fetchVrUsers: () => void;
};

const UserActionFormModal: React.FC<Props> = ({
  user,
  fetchVrUsers,
  schools,
  vrUsers
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state): Admin | null => state.auth.user);
  const { isModalOpened, isModalDataLoading } = useAppSelector(
    (state): ModalState => state.modal
  );

  const [domain, setDomain] = useState(
    !!user && user.email ? '@' + user.email.split('@')[1] : ''
  );

  function getSchoolId(school: School | string | undefined): string {
    if (!school) return '';
    if (typeof school === 'string') {
      return school.split('/').pop() || '';
    }
    return school.id || '';
  }

  const availableDomains = useMemo(() => {
    const productDSchools = schools.filter((school) => school.data_source === 'product_d');
    const filteredSchools =
      currentUser?.role === EAdminRoles.SCHOOL_ADMIN
        ? productDSchools.filter((school) => school.id === getSchoolId(currentUser.school))
        : productDSchools;

    const entries = filteredSchools.flatMap((school: School) => {
      const rawDomains = typeof school.domains === 'string'
        ? [school.domains]
        : school.domains ?? [];
      const schoolName = school.name || school.Name || '';
      return rawDomains
        .filter(Boolean)
        .map((rawDomain) => {
          const domain = /^@/.test(rawDomain) ? rawDomain : `@${rawDomain}`;
          return {
            value: `${domain}|${getSchoolId(school.id)}`,
            domain,
            schoolName,
          };
        });
    });

    return _.uniqBy(entries, 'value');
  }, [schools, currentUser]);

  const handleCancel = (): void => {
    dispatch(closeModal());
  };

  const initialValues: TableVrUser = {
    firebaseId: user?.firebaseId || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    emailName: user?.email?.split('@')?.[0] || '',
    email: user?.email || '',
    OrganizationId: user
      ? user.OrganizationId || getSchoolId(user.school)
      : '',
    id: String(user?.id) || '',
    key: String(user?.id) || '',
  };

  const formik = useFormik({
    onSubmit: async (values): Promise<void> => {
      const newData = _.omit(values, ['key', 'firebaseId']);

      await dispatch(
        !!user && user.id !== undefined
          ? updateUserItem({
              userId: user.id,
              values: {
                ...newData,
                school: { id: newData.OrganizationId },
              }
            })
          : addNewUser({
              values: {
                ...newData,
                school: { id: newData.OrganizationId },
                isStudent: true,
              }
            })
      );

      dispatch(closeModal());
      fetchVrUsers();
    },
    initialValues,
    validationSchema: !!user ? validation.UPDATE_VR_USER : validation.CREATE_VR_USER,
    enableReinitialize: true
  });

  const handleIdFieldChange = (event: InputEvent): void => {
    const { value, name } = event.target;

    if (name === 'emailName') {
      const sanitizedValue = value.split('@')[0];

      formik.setFieldValue('emailName', sanitizedValue);
      formik.setFieldValue('email', sanitizedValue + domain);
      return;
    }

    formik.setFieldValue(name, value);
  };

  useEffect(() => {
    if (
      currentUser?.role === EAdminRoles.SCHOOL_ADMIN &&
      !formik.values.OrganizationId
    ) {
      formik.setFieldValue('OrganizationId', getSchoolId(currentUser.school));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (formik.values.emailName && domain) {
      const composedEmail = `${formik.values.emailName}${domain}`;
      if (formik.values.email !== composedEmail) {
        formik.setFieldValue('email', composedEmail);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.emailName, domain]);

  return (
    <Modal
      open={isModalOpened}
      width={720}
      title={!!user ? 'Edit User' : 'Add New User'}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={(): void => formik.handleSubmit()}
          disabled={isModalDataLoading}
        >
          Save
        </Button>
      ]}
    >
      <form onSubmit={formik.handleSubmit}>
        <Styles.InputContainer>
          <Input
            name="firstName"
            value={getIn(formik.values, 'firstName')}
            onBlur={formik.handleBlur}
            onChange={handleIdFieldChange}
            placeholder="First name"
            disabled={isModalDataLoading}
            allowClear
          />
          {getFieldError(formik, 'firstName')}
        </Styles.InputContainer>

        <Styles.InputContainer>
          <Input
            name="lastName"
            value={getIn(formik.values, 'lastName')}
            onBlur={formik.handleBlur}
            onChange={handleIdFieldChange}
            placeholder="Last name"
            disabled={isModalDataLoading}
            allowClear
          />
          {getFieldError(formik, 'lastName')}
        </Styles.InputContainer>

        <Styles.InputContainer>
          <Input
            name="emailName"
            value={getIn(formik.values, 'emailName')}
            onBlur={formik.handleBlur}
            onChange={handleIdFieldChange}
            placeholder="Email"
            disabled={isModalDataLoading}
            allowClear
            style={{ width: '35%' }}
          />
          <Select
            style={{ marginLeft: '23px', width: '60%' }}
            popupMatchSelectWidth={false}
            onChange={(value) => {
              const [domain, OrganizationId] = value.split('|');
              setDomain(domain);
              formik.setFieldValue(
                'email',
                getIn(formik.values, 'emailName') + domain
              );
              formik.setFieldValue('OrganizationId', OrganizationId);
            }}
            placeholder="School domain"
            disabled={isModalDataLoading}
            value={domain}
          >
            <Select.Option value="">{'Select a domain'}</Select.Option>
            {availableDomains.map((entry) => (
              <Select.Option key={entry.value} value={entry.value}>
                {entry.schoolName ? `${entry.domain} — ${entry.schoolName}` : entry.domain}
              </Select.Option>
            ))}
          </Select>
          {getFieldError(formik, 'email')}
        </Styles.InputContainer>
      </form>
    </Modal>
  );
};

export default UserActionFormModal;
